import { Search } from "lucide-react";
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ComponentProps,
  type KeyboardEvent,
} from "react";
import { cn } from "../lib/cn";

/**
 * Command palette primitives. Generic on purpose: the caller owns the query
 * (`value` / `onValueChange`) and decides what to render. Filtering is either
 * done by the caller (only render matching items) or through the optional
 * `filter` prop. Keyboard: arrows move, Enter selects, Escape calls `onEscape`.
 * The input keeps focus; the active item is announced via `aria-activedescendant`.
 */

export type CommandFilter = (value: string, query: string, keywords?: readonly string[]) => boolean;

/** Case-insensitive substring match over the value and keywords. */
export const defaultCommandFilter: CommandFilter = (value, query, keywords) => {
  const q = query.trim().toLowerCase();
  if (q === "") return true;
  if (value.toLowerCase().includes(q)) return true;
  return keywords?.some((k) => k.toLowerCase().includes(q)) ?? false;
};

interface CommandStore {
  register(id: string, element: HTMLElement): () => void;
  setActive(id: string | null): void;
  getActiveId(): string | null;
  getElement(id: string): HTMLElement | undefined;
  getOrderedIds(): readonly string[];
  getCount(): number;
  subscribe(listener: () => void): () => void;
}

function createCommandStore(): CommandStore {
  const items = new Map<string, HTMLElement>();
  const listeners = new Set<() => void>();
  let active: string | null = null;
  let ordered: readonly string[] | null = null;

  const notify = (): void => {
    for (const listener of listeners) listener();
  };

  const getOrderedIds = (): readonly string[] => {
    if (ordered === null) {
      ordered = [...items.entries()]
        .sort(([, a], [, b]) =>
          a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1,
        )
        .map(([id]) => id);
    }
    return ordered;
  };

  return {
    register(id, element) {
      items.set(id, element);
      ordered = null;
      notify();
      return () => {
        items.delete(id);
        ordered = null;
        notify();
      };
    },
    setActive(id) {
      if (active === id) return;
      active = id;
      notify();
    },
    getActiveId() {
      if (active !== null && items.has(active)) return active;
      return getOrderedIds()[0] ?? null;
    },
    getElement: (id) => items.get(id),
    getOrderedIds,
    getCount: () => items.size,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

interface CommandContextValue {
  store: CommandStore;
  query: string;
  filter: CommandFilter | undefined;
  listId: string;
  onValueChange: (value: string) => void;
}

const CommandContext = createContext<CommandContextValue | null>(null);

function useCommandContext(part: string): CommandContextValue {
  const ctx = useContext(CommandContext);
  if (ctx === null) throw new Error(`Command.${part} must be rendered inside Command.Root`);
  return ctx;
}

export interface CommandRootProps extends Omit<ComponentProps<"div">, "onSelect"> {
  /** The search query (controlled). */
  value: string;
  onValueChange: (value: string) => void;
  /** Optional item filter. Omit it to render only matching items yourself. */
  filter?: CommandFilter;
  /** Called on Escape. Typically closes the surrounding dialog. */
  onEscape?: () => void;
  /** Wrap from last to first item and back. Default true. */
  loop?: boolean;
}

function Root({
  value,
  onValueChange,
  filter,
  onEscape,
  loop = true,
  className,
  onKeyDown,
  children,
  ...props
}: CommandRootProps) {
  const [store] = useState(createCommandStore);
  const listId = useId();

  const move = (delta: number | "first" | "last"): void => {
    const ids = store.getOrderedIds();
    if (ids.length === 0) return;
    let next: string | undefined;
    if (delta === "first") next = ids[0];
    else if (delta === "last") next = ids[ids.length - 1];
    else {
      const current = store.getActiveId();
      const index = current === null ? -1 : ids.indexOf(current);
      let target = index + delta;
      if (target < 0) target = loop ? ids.length - 1 : 0;
      if (target >= ids.length) target = loop ? 0 : ids.length - 1;
      next = ids[target];
    }
    if (next !== undefined) store.setActive(next);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        move(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        move(-1);
        break;
      case "Home":
        event.preventDefault();
        move("first");
        break;
      case "End":
        event.preventDefault();
        move("last");
        break;
      case "Enter": {
        const activeId = store.getActiveId();
        if (activeId === null) return;
        event.preventDefault();
        store.getElement(activeId)?.click();
        break;
      }
      case "Escape":
        if (onEscape === undefined) return;
        event.preventDefault();
        onEscape();
        break;
      default:
    }
  };

  return (
    <CommandContext.Provider value={{ store, query: value, filter, listId, onValueChange }}>
      <div
        data-slot="command"
        className={cn(
          "flex h-full w-full flex-col overflow-hidden bg-surface-raised text-fg",
          className,
        )}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {children}
      </div>
    </CommandContext.Provider>
  );
}

export interface CommandInputProps extends Omit<
  ComponentProps<"input">,
  "value" | "onChange" | "type"
> {}

function Input({ className, ...props }: CommandInputProps) {
  const { store, query, listId, onValueChange } = useCommandContext("Input");
  const activeId = useSyncExternalStore(store.subscribe, store.getActiveId, store.getActiveId);
  return (
    <div
      data-slot="command-input-wrapper"
      className="flex items-center gap-2 border-b border-border px-3"
    >
      <Search className="size-4 shrink-0 text-fg-faint" aria-hidden="true" />
      <input
        data-slot="command-input"
        type="text"
        role="combobox"
        aria-expanded="true"
        aria-controls={listId}
        aria-activedescendant={activeId ?? undefined}
        aria-autocomplete="list"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        value={query}
        onChange={(event) => {
          store.setActive(null);
          onValueChange(event.target.value);
        }}
        className={cn(
          "h-10 w-full min-w-0 bg-transparent text-sm text-fg outline-none placeholder:text-fg-faint disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...props}
      />
    </div>
  );
}

function List({ className, ...props }: ComponentProps<"div">) {
  const { listId } = useCommandContext("List");
  return (
    <div
      data-slot="command-list"
      id={listId}
      role="listbox"
      className={cn("max-h-80 overflow-y-auto overscroll-contain p-1", className)}
      {...props}
    />
  );
}

function Empty({ className, ...props }: ComponentProps<"div">) {
  const { store } = useCommandContext("Empty");
  const count = useSyncExternalStore(store.subscribe, store.getCount, store.getCount);
  if (count > 0) return null;
  return (
    <div
      data-slot="command-empty"
      role="presentation"
      className={cn("py-6 text-center text-sm text-fg-muted", className)}
      {...props}
    />
  );
}

export interface CommandGroupProps extends ComponentProps<"div"> {
  heading?: React.ReactNode;
}

function Group({ heading, className, children, ...props }: CommandGroupProps) {
  const headingId = useId();
  return (
    <div
      data-slot="command-group"
      role="group"
      aria-labelledby={heading === undefined ? undefined : headingId}
      className={cn("hidden has-data-[slot=command-item]:block", className)}
      {...props}
    >
      {heading !== undefined && (
        <div
          id={headingId}
          data-slot="command-group-heading"
          className="px-2 pt-2 pb-1 text-xs font-medium text-fg-muted"
        >
          {heading}
        </div>
      )}
      {children}
    </div>
  );
}

export interface CommandItemProps extends Omit<ComponentProps<"div">, "onSelect"> {
  /** Identifies the item; also what `filter` matches against. */
  value: string;
  /** Extra strings `filter` may match. */
  keywords?: readonly string[];
  onSelect?: (value: string) => void;
  disabled?: boolean;
}

function Item({
  value,
  keywords,
  onSelect,
  disabled = false,
  className,
  onClick,
  onPointerMove,
  children,
  ...props
}: CommandItemProps) {
  const { store, query, filter } = useCommandContext("Item");
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const visible = filter === undefined ? true : filter(value, query, keywords);
  const navigable = visible && !disabled;

  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null || !navigable) return undefined;
    return store.register(id, element);
  }, [store, id, navigable]);

  const active = useSyncExternalStore(
    store.subscribe,
    () => navigable && store.getActiveId() === id,
    () => false,
  );

  useEffect(() => {
    const element = ref.current;
    if (active && element !== null && typeof element.scrollIntoView === "function") {
      element.scrollIntoView({ block: "nearest" });
    }
  }, [active]);

  if (!visible) return null;

  return (
    <div
      ref={ref}
      id={id}
      data-slot="command-item"
      data-value={value}
      data-active={active ? "" : undefined}
      data-disabled={disabled ? "" : undefined}
      role="option"
      aria-selected={active}
      aria-disabled={disabled || undefined}
      className={cn(
        "relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-fg outline-none select-none data-active:bg-fg/6 data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-fg-muted",
        className,
      )}
      onPointerMove={(event) => {
        onPointerMove?.(event);
        if (navigable) store.setActive(id);
      }}
      onClick={(event) => {
        onClick?.(event);
        if (!disabled && !event.defaultPrevented) onSelect?.(value);
      }}
      {...props}
    >
      {children}
    </div>
  );
}

function Separator({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="command-separator"
      role="separator"
      className={cn("-mx-1 my-1 h-px bg-border", className)}
      {...props}
    />
  );
}

export const Command = { Root, Input, List, Empty, Group, Item, Separator };
